<div class="scrap-collection-detail-container">
  <!-- Back Button and Header -->
  <div class="top-bar">
    <button class="back-btn" (click)="goBack()">
      <i class="fas fa-arrow-left"></i> Back to List
    </button>
  </div>

  <div class="header">
    <div class="header-content">
      <div>
        <h1>Scrap Collection Details</h1>
        <p *ngIf="collection">Collection ID: {{ collection.collectionId || collection.id }}</p>
      </div>
      <div class="header-actions">
        <button class="download-excel-btn" (click)="downloadCSV()" *ngIf="collection" title="Download as CSV">
          <i class="fas fa-file-csv"></i> Download CSV
        </button>
        <button class="btn-convert-cash-header" (click)="openConvertToCashDialog()" *ngIf="canConvertToCash()" title="Convert Payment Method to Cash / UPI">
          <i class="fas fa-money-bill-wave"></i> Convert to Cash
        </button>
        <div class="header-status" *ngIf="collection">
          <span class="status-badge" [class.status-valid]="isCollectionValid()"
            [class.status-invalid]="!isCollectionValid()">
            <i class="fas" [class.fa-check-circle]="isCollectionValid()"
              [class.fa-times-circle]="!isCollectionValid()"></i>
            {{ isCollectionValid() ? 'Valid Collection' : 'Invalid Collection' }}
          </span>
        </div>
      </div>
    </div>
  </div>

  <!-- Success Message -->
  <div *ngIf="convertSuccess" class="error-message bg-emerald-50 border-emerald-400 text-emerald-800">
    <i class="fas fa-check-circle mr-2"></i>
    {{ convertSuccess }}
  </div>

  <!-- Error Message -->
  <div *ngIf="errorMessage" class="error-message">
    <i class="fas fa-exclamation-circle mr-2"></i>
    {{ errorMessage }}
    <button class="back-btn-inline" (click)="goBack()">Return to List</button>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="loading-state">
    <div class="loading-spinner"></div>
    <span class="loading-text">Loading collection details...</span>
  </div>

  <!-- Collection Details -->
  <div *ngIf="!loading && collection" class="details-container">
    <!-- Customer Information -->
    <div class="details-card">
      <div class="card-header">
        <h2><i class="fas fa-user"></i> Customer Information</h2>
      </div>
      <div class="card-body">
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Name:</span>
            <span class="info-value">{{ collection.customerName || 'N/A' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Phone Number:</span>
            <span class="info-value">{{ collection.customerPhoneNumber || 'N/A' }}</span>
          </div>
          <div class="info-item full-width">
            <span class="info-label">Address:</span>
            <span class="info-value">{{ collection.address || 'N/A' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Location:</span>
            <span class="info-value">{{ collection.locationName || 'N/A' }}</span>
          </div>
          <div class="info-item" *ngIf="collection.location?.latitude && collection.location?.longitude">
            <span class="info-label">Map:</span>
            <a [href]="getGoogleMapsUrl()" target="_blank" class="info-link">
              <i class="fas fa-map-marker-alt"></i> View on Google Maps
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Collection Information -->
    <div class="details-card">
      <div class="card-header">
        <h2><i class="fas fa-clipboard-list"></i> Collection Information</h2>
      </div>
      <div class="card-body">
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Collection Date:</span>
            <span class="info-value">{{ formatDate(collection.date) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Zone:</span>
            <span class="info-value zone-badge">{{ getZoneName(collection.geoZoneId) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Appointment ID:</span>
            <span class="info-value">{{ collection.appointmentId || 'N/A' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Collection ID:</span>
            <span class="info-value">{{ collection.collectionId || 'N/A' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Agent Information -->
    <div class="details-card" *ngIf="collection.agentDetails">
      <div class="card-header">
        <h2><i class="fas fa-user-tie"></i> Agent Information</h2>
      </div>
      <div class="card-body">
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Agent Name:</span>
            <span class="info-value">{{ collection.agentDetails.name || 'N/A' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Phone Number:</span>
            <span class="info-value">{{ collection.agentDetails.phoneNumber || 'N/A' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Vehicle Number:</span>
            <span class="info-value">{{ collection.agentDetails.vehicleNumber || 'N/A' }}</span>
          </div>
          <div class="info-item" *ngIf="collection.agentDetails.agentRating">
            <span class="info-label">Rating:</span>
            <span class="info-value">
              <i class="fas fa-star rating-star"></i> {{ collection.agentDetails.agentRating }} / 5
            </span>
          </div>
          <div class="info-item full-width" *ngIf="collection.agentDetails.customerFeedback">
            <span class="info-label">Customer Feedback:</span>
            <span class="info-value">{{ collection.agentDetails.customerFeedback }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Financial Summary -->
    <div class="details-card financial-card">
      <div class="card-header">
        <h2><i class="fas fa-money-bill-wave"></i> Financial Summary</h2>
      </div>
      <div class="card-body">
        <div class="financial-summary-simple">
          <div class="financial-row">
            <span class="financial-label">Total Items:</span>
            <span class="financial-value">{{ collection.totalItems }}</span>
          </div>
          <div class="financial-row">
            <span class="financial-label">Total Scrap Price:</span>
            <span class="financial-value">{{ formatCurrency(collection.totalScrapPrice) }}</span>
          </div>
          <div class="financial-row deduction">
            <span class="financial-label">Total Non-Scrap Price:</span>
            <span class="financial-value">- {{ formatCurrency(collection.totalNonScrapPrice) }}</span>
          </div>
          <div class="financial-row deduction">
            <span class="financial-label">Collection Charges:</span>
            <span class="financial-value">- {{ formatCurrency(collection.collectionCharges) }}</span>
          </div>
          <div class="financial-row deduction">
            <span class="financial-label">Platform Charges:</span>
            <span class="financial-value">- {{ formatCurrency(collection.platformCharges) }}</span>
          </div>
          <div class="financial-row">
            <span class="financial-label">Payment Method:</span>
            <span class="financial-value font-semibold" [ngClass]="isVoucherPayment() ? 'text-amber-600' : 'text-emerald-700'">
              {{ getPaymentMethodName(collection.paymentMethod) }}
            </span>
          </div>
          <div class="financial-row total-row">
            <span class="financial-label">Grand Total:</span>
            <span class="financial-value">{{ formatCurrency(collection.grandTotal) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Card - Convert Payment Method to Cash -->
    <div class="details-card action-card shadow-md border-2 border-amber-300" *ngIf="canConvertToCash()">
      <div class="card-header bg-gradient-to-r from-amber-500 to-orange-600 text-white p-4 rounded-t-xl flex justify-between items-center">
        <h2><i class="fas fa-exchange-alt mr-2"></i> Action Required: Convert Payment to Cash / UPI</h2>
        <span class="bg-amber-700 text-white text-xs px-2.5 py-1 rounded-full font-bold uppercase tracking-wider">Voucher Eligible</span>
      </div>
      <div class="card-body p-4 bg-amber-50/30">
        <div class="warning-content">
          <p class="warning-text text-gray-800 mb-2">
            <strong>Customer Changed Mind?</strong> If the customer requests cash/UPI payment instead of their store voucher, you can switch it here immediately.
          </p>
          <p class="warning-text text-gray-600 mb-4 text-sm">
            Conversion will automatically invalidate and cancel active vouchers, recalculate the collection at standard cash pricing, and adjust any agent commission settlements via ledger entries.
          </p>
          <button class="mark-invalid-btn bg-amber-600 hover:bg-amber-700 border-amber-600 text-white font-semibold py-2.5 px-5 rounded-lg shadow transition duration-200" (click)="openConvertToCashDialog()" [disabled]="convertingToCash">
            <i class="fas fa-money-bill-wave mr-1"></i> Convert Payment Method to Cash / UPI
          </button>
        </div>
      </div>
    </div>

    <!-- Items Collected -->
    <div class="details-card" *ngIf="collection.collectionDetails && collection.collectionDetails.length > 0">
      <div class="card-header">
        <h2><i class="fas fa-boxes"></i> Items Collected</h2>
      </div>
      <div class="card-body">
        <div class="table-responsive">
          <table class="items-table">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Quantity</th>
                <th>Deducted</th>
                <th>Net Quantity</th>
                <th>Market Price</th>
                <th>Total Price</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of collection.collectionDetails">
                <td class="item-name">{{ item.scrapItemName }}</td>
                <td>{{ item.quantity }} {{ item.unit }}</td>
                <td class="deducted">{{ item.deductedQuantity || 0 }} {{ item.unit }}</td>
                <td class="net-quantity">{{ item.quantity - (item.deductedQuantity || 0) }} {{ item.unit }}</td>
                <td>{{ item.unitPrice ? formatCurrency(item.unitPrice) : 'N/A' }}</td>
                <td class="total-price">{{ formatCurrency(item.totalPrice) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>



    <!-- Customer Care Feedback Information -->
    <div class="details-card" *ngIf="collection.requiresFeedback || collection.feedbackDetails">
      <div class="card-header bg-gradient-to-r from-emerald-600 to-teal-700 text-white p-4 rounded-t-xl">
        <h2><i class="fas fa-headset mr-2"></i> Customer Care Call Feedback</h2>
      </div>
      <div class="card-body">
        <div class="info-grid">
          <div class="info-item" *ngIf="collection.feedbackDetails?.feedbackReason">
            <span class="info-label">Tag Reason:</span>
            <span class="info-value font-semibold text-amber-800">{{ collection.feedbackDetails?.feedbackReason }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Call Outcome Status:</span>
            <span class="info-value font-bold" [ngClass]="{
              'text-emerald-700': collection.feedbackDetails?.feedbackStatus === 'Completed',
              'text-amber-700': collection.feedbackDetails?.feedbackStatus === 'Pending' || !collection.feedbackDetails?.feedbackStatus,
              'text-gray-600': collection.feedbackDetails?.feedbackStatus === 'Skipped'
            }">{{ collection.feedbackDetails?.feedbackStatus || 'Pending' }}</span>
          </div>
          <div class="info-item" *ngIf="collection.feedbackDetails?.happinessIndex">
            <span class="info-label">Happiness Index:</span>
            <span class="info-value font-bold text-emerald-700">
              <i class="fas fa-star text-amber-400 mr-1"></i> {{ collection.feedbackDetails?.happinessIndex }} / 10
            </span>
          </div>
          <div class="info-item" *ngIf="collection.feedbackDetails?.loggedAt">
            <span class="info-label">Logged At:</span>
            <span class="info-value">{{ formatDate(collection.feedbackDetails?.loggedAt!) }}</span>
          </div>
          <div class="info-item full-width" *ngIf="collection.feedbackDetails?.customerComments">
            <span class="info-label">Customer Comments:</span>
            <span class="info-value italic text-gray-700 font-normal">"{{ collection.feedbackDetails?.customerComments }}"</span>
          </div>
          <div class="info-item full-width" *ngIf="collection.feedbackDetails?.internalComments">
            <span class="info-label">Internal Agent Notes:</span>
            <span class="info-value text-gray-700 font-normal">{{ collection.feedbackDetails?.internalComments }}</span>
          </div>
          <div class="info-item full-width" *ngIf="collection.feedbackDetails?.questionAnswers && collection.feedbackDetails!.questionAnswers!.length > 0">
            <span class="info-label">Survey Answers:</span>
            <div class="mt-2 space-y-2">
              <div *ngFor="let qa of collection.feedbackDetails?.questionAnswers" class="bg-gray-50 p-2 rounded border border-gray-200 text-xs">
                <span class="font-bold text-gray-800">{{ qa.questionText }}:</span>
                <span class="ml-2 font-semibold text-emerald-800">{{ qa.answer }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Card - Mark as Invalid -->
    <div class="details-card action-card" *ngIf="canMarkInvalid()">
      <div class="card-header warning-header">
        <h2><i class="fas fa-exclamation-triangle"></i> Mark Collection as Invalid</h2>
      </div>
      <div class="card-body">
        <div class="warning-content">
          <p class="warning-text">
            <strong>Warning:</strong> Marking this collection as invalid is a permanent action that cannot be undone.
            This should only be done if there are errors made by the agent during the scrap collection fulfillment.
          </p>
          <p class="warning-text">
            Invalid collections will be kept in the database for historical and audit purposes,
            allowing the agent to create a new collection against the same appointment.
          </p>
          <button class="mark-invalid-btn" (click)="openConfirmDialog()" [disabled]="togglingInvalid">
            <i class="fas fa-ban"></i> Mark as Invalid
          </button>
        </div>
      </div>
    </div>

    <!-- Already Invalid Notice -->
    <div class="details-card invalid-notice-card" *ngIf="!isCollectionValid()">
      <div class="card-header invalid-header">
        <h2><i class="fas fa-info-circle"></i> Collection Status</h2>
      </div>
      <div class="card-body">
        <div class="invalid-notice">
          <i class="fas fa-times-circle"></i>
          <div>
            <h3>This collection has been marked as invalid</h3>
            <p>This collection is kept in the system for historical and audit purposes. The agent can create a new
              collection against the associated appointment.</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Mark Invalid Confirmation Dialog -->
  <div *ngIf="showConfirmDialog" class="modal-overlay" (click)="closeConfirmDialog()">
    <div class="modal-content confirm-dialog" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2><i class="fas fa-exclamation-triangle"></i> Confirm Action</h2>
        <button class="modal-close-btn" (click)="closeConfirmDialog()" [disabled]="togglingInvalid">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <p class="confirm-message">
          Are you sure you want to mark this scrap collection as <strong>invalid</strong>?
        </p>
        <p class="confirm-warning">
          This action is <strong>permanent</strong> and cannot be reversed. Please ensure you have reviewed all the
          collection details carefully.
        </p>

        <div *ngIf="toggleError" class="error-message-modal">
          <i class="fas fa-exclamation-circle"></i> {{ toggleError }}
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" (click)="closeConfirmDialog()" [disabled]="togglingInvalid">
          Cancel
        </button>
        <button class="btn-confirm-invalid" (click)="confirmMarkInvalid()" [disabled]="togglingInvalid">
          <span *ngIf="togglingInvalid">
            <i class="fas fa-spinner fa-spin"></i> Processing...
          </span>
          <span *ngIf="!togglingInvalid">
            <i class="fas fa-ban"></i> Yes, Mark as Invalid
          </span>
        </button>
      </div>
    </div>
  </div>

  <!-- Convert to Cash Confirmation Dialog -->
  <div *ngIf="showConvertToCashDialog" class="modal-overlay" (click)="closeConvertToCashDialog()">
    <div class="modal-content confirm-dialog" (click)="$event.stopPropagation()">
      <div class="modal-header bg-amber-600 text-white">
        <h2><i class="fas fa-money-bill-wave"></i> Confirm Payment Conversion to Cash / UPI</h2>
        <button class="modal-close-btn text-white" (click)="closeConvertToCashDialog()" [disabled]="convertingToCash">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <p class="confirm-message text-gray-800">
          Are you sure you want to convert collection <strong>{{ collection?.collectionId || collection?.id }}</strong> from <strong>Voucher</strong> to <strong>Cash / UPI</strong>?
        </p>
        <div class="bg-amber-50 border border-amber-200 p-3 rounded-lg text-xs text-amber-900 space-y-1 my-3">
          <p><strong>Impact of conversion:</strong></p>
          <p>• All active vouchers associated with this collection will be <strong>cancelled</strong>.</p>
          <p>• If any voucher has already been redeemed or revoked, conversion will be blocked.</p>
          <p>• Agent voucher commissions will be reset, and settled commissions clawed back via Agent Ledger.</p>
          <p>• Grand Total will be recalculated under standard cash rates.</p>
        </div>

        <div *ngIf="convertError" class="error-message-modal">
          <i class="fas fa-exclamation-circle"></i> {{ convertError }}
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" (click)="closeConvertToCashDialog()" [disabled]="convertingToCash">
          Cancel
        </button>
        <button class="btn-confirm-invalid bg-amber-600 hover:bg-amber-700 text-white border-amber-600" (click)="confirmConvertToCash()" [disabled]="convertingToCash">
          <span *ngIf="convertingToCash">
            <i class="fas fa-spinner fa-spin"></i> Converting...
          </span>
          <span *ngIf="!convertingToCash">
            <i class="fas fa-check"></i> Yes, Convert to Cash
          </span>
        </button>
      </div>
    </div>
  </div>
</div>